import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { 
  AddReleaseIssueRequest, AddReleaseIssueResponse, AssignReleaseIssueTagRequest, AssignReleaseIssueTagResponse, BulkUpdateReleaseItemsRequest, BulkUpdateReleaseItemsResponse, GetReleaseIssueRequest, GetReleaseIssueResponse, GetSituFacadeRequest, 
  GetSituFacadeResponse, ListReleaseIssuesRequest, ListReleaseIssuesResponse, ListReleaseItemsRequest, ListReleaseItemsResponse, 
  ListTagsRequest, 
  ListTagsResponse, 
  ProductName, ReleaseIssue, ReleaseIssueCategory, ReleaseIssuePriority, ReleaseIssueStatus, ReleaseIssueTag, ReleaseItem, ReleaseItemFieldToUpdate, RemoveReleaseIssueTagRequest, RemoveReleaseIssueTagResponse, ResourceType, SituViewWebApiClient, Tag, UpdateReleaseIssueRequest, 
  UpdateReleaseIssueResponse
} from 'situview-webapi-client';
import { FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Company, IdentityWebApiClient, ListUsersRequest, ListUsersResponse } from 'identity-webapi-client';
import { SelectableItem } from 'src/app/shared/models/selectable-item';
import { AuthService } from 'src/app/services';
import { Guid } from 'guid-typescript';
import { EWOULDBLOCK } from 'constants';

@Component({
  selector: 'release-issue-dialog',
  templateUrl: './release-issue-dialog.component.html',
  styleUrls: ['./release-issue-dialog.component.scss']
})
export class ReleaseIssueDialogComponent {
  releaseIssueId: string | undefined;
  releaseIssue: ReleaseIssue | undefined;
  currentDate: Date | undefined;
  currentUser: string | undefined;

  selectedPriority: ReleaseIssuePriority | undefined;
  releaseIssuePriorities = ReleaseIssuePriority;
  releaseIssueStatuses = ReleaseIssueStatus;
  releaseIssueCategories = ReleaseIssueCategory;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ReleaseIssueDialogComponentData,
    private dialogRef: MatDialogRef<ReleaseIssueDialogComponent>,
    private snackBar: MatSnackBar,
    private situViewApiClient: SituViewWebApiClient,
    private identityWebApiClient: IdentityWebApiClient,
    private authService: AuthService) {
    this.releaseIssueId = this.data.releaseIssueId;
    if (this.releaseIssueId) {
      this.loadReleaseIssue(this.releaseIssueId);
      this.confirmedNewIssue = true;
    } else {
      this.releaseIssue = new ReleaseIssue();
      this.isTitleEditMode = true;
      this.releaseIssue.reportedSituLink = data.reportedSituLink;
      this.releaseIssue.releaseIssueStatus = this.releaseIssueStatuses.Unassigned;
      this.releaseIssue.releaseIssueCategory = this.releaseIssueCategories.Drawing;
      this.loadReleaseIssues();
      this.loadReleaseItems();
      this.currentDate = new Date();
      this.currentUser = this.authService.getUserName();
    }
    this.loadUsers();
  }

  isTitleEditMode = false;
  isDescriptionEditMode = false;
  saveTitle() {
    this.isTitleEditMode = false;
    if (!this.releaseIssue?.description) this.isDescriptionEditMode = true;
  }
  saveDescription() {
    this.isDescriptionEditMode = false;
  }
  closeDialog(): void {
    this.dialogRef.close();
  }

  selectedCard: string | undefined;
  onCardSelectClick(releaseIssueId: string | undefined) { 
    if (releaseIssueId !== this.selectedCard) {
      this.selectedCard = releaseIssueId;
    } else {
      this.selectedCard = undefined;
    }
  }
  addToReleaseIssue() {
    if (this.selectedCard) {
      let request = new BulkUpdateReleaseItemsRequest();
      request.releaseItemIds = this.data.linkedReleaseItemIds;
      request.companyId = this.data.companyId;
      request.releaseItemFieldToUpdate = ReleaseItemFieldToUpdate.AssignToReleaseIssue;
      request.releaseIssueId = this.selectedCard;
  
      this.situViewApiClient.bulkUpdateReleaseItems(request)
        .then((response: BulkUpdateReleaseItemsResponse) => {
          if (response.success) {
            this.snackBar.open(`Successfully added home configuration to Release Issue`, 'OK', { duration: 2000 });
            this.dialogRef.close();
          } else {
            this.snackBar.open(`Could not update the release items. Refresh Page.`, 'OK', { duration: 2000 });
          }
        })
        .catch((err: any) => {
          this.snackBar.open(`Could not update the release items. Refresh Page.`, 'OK', { duration: 2000 });
        });
    }
  }

  getPriority(releaseIssuePriority: ReleaseIssuePriority) {
    switch(releaseIssuePriority) {
      case ReleaseIssuePriority.High:
        return "Blocker";
      case ReleaseIssuePriority.Medium:
        return "Medium";
      default: 
        return "Low";
    }
  }
  
  getCategory(releaseIssueCategory: ReleaseIssueCategory) {
    switch(releaseIssueCategory) {
      case ReleaseIssueCategory.Theming:
        return "Style / Theme";
      case ReleaseIssueCategory.Drawing:
        return "Home Plan";
      default: 
        return "Software";
    }
  }

  updateReleaseIssue() {
    let request = new UpdateReleaseIssueRequest();
    request.companyId = this.data.companyId;
    request.releaseIssueId = this.releaseIssue?.id;
    request.title = this.releaseIssue?.title;
    request.description = this.releaseIssue?.description;
    request.releaseIssuePriority = this.releaseIssue?.releaseIssuePriority;
    request.releaseIssueStatus = this.releaseIssue?.releaseIssueStatus;
    request.releaseIssueCategory = this.releaseIssue?.releaseIssueCategory;
    request.reportedSituLink = this.releaseIssue?.reportedSituLink;
    request.assignedTo = this.releaseIssue?.assignedTo;
    request.assignedToName = this.releaseIssue?.assignedToName;

    this.situViewApiClient.updateReleaseIssue(request)
        .then((response: UpdateReleaseIssueResponse) => {
            if (response.success) {
                this.snackBar.open(`Updated release issue!`, 'OK', { duration: 2000 });
                let result = new ReleaseIssueDialogComponentResultData();
                result.refreshNeeded = true;
                this.dialogRef.close(result);
            } else {
                this.snackBar.open(`Error updating release issue!`, 'OK', { duration: 2000 });
            }
        })
        .catch((err: any) => {
            this.snackBar.open(`Error updating release issue!`, 'OK', { duration: 2000 });
        });
  }
  addReleaseIssue() {
    let request = new AddReleaseIssueRequest();
    request.companyId = this.data.companyId;
    request.title = this.releaseIssue?.title;
    request.description = this.releaseIssue?.description;
    request.releaseIssuePriority = this.releaseIssue?.releaseIssuePriority;
    request.releaseIssueCategory = this.releaseIssue?.releaseIssueCategory;
    request.releaseItemId = this.data?.releaseId;
    request.reportedSituLink = this.releaseIssue?.reportedSituLink;
    request.assignedTo = this.releaseIssue?.assignedTo;
    request.assignedToName = this.releaseIssue?.assignedToName;
    request.releaseItemId = this.releaseItems.find(() => true)?.id;
    request.productName = this.data?.reportedSituLinkProductName;

    this.situViewApiClient.addReleaseIssue(request)
        .then((response: AddReleaseIssueResponse) => {
            if (response.success) {
                this.snackBar.open(`Added release issue!`, 'OK', { duration: 2000 });
                let result = new ReleaseIssueDialogComponentResultData();
                result.refreshNeeded = true;
                this.dialogRef.close(result);
            } else {
                this.snackBar.open(`Error adding release issue!`, 'OK', { duration: 2000 });
            }
        })
        .catch((err: any) => {
            this.snackBar.open(`Error adding release issue!`, 'OK', { duration: 2000 });
        });
  }

  selectPriority(priority: ReleaseIssuePriority) {
    if (this.releaseIssue) {
      this.releaseIssue.releaseIssuePriority = priority;
    }
  }  
  selectCategory(category: ReleaseIssueCategory) {
    if (this.releaseIssue) {
      this.releaseIssue.releaseIssueCategory = category;
    }
  }

  leftStatusChangeAvailable: boolean = false;
  leftStatus: ReleaseIssueStatus | undefined;
  rightStatusChangeAvailable: boolean = false;
  rightStatus: ReleaseIssueStatus | undefined;
  selectStatus(status: ReleaseIssueStatus) {
    if (this.releaseIssue) {
      this.releaseIssue.releaseIssueStatus = status;
      this.determineAvailableStatusChanges();
    }
  }
  determineAvailableStatusChanges() {
    this.resetStatusMarkers();
    if (this.releaseIssueId && this.releaseIssue?.releaseIssueStatus) {
      switch(this.releaseIssue.releaseIssueStatus) {
        //case ReleaseIssueStatus.Unassigned:
        case ReleaseIssueStatus.Assigned:
          this.rightStatus = ReleaseIssueStatus.InProgress;
          this.rightStatusChangeAvailable = true;
          break;
        case ReleaseIssueStatus.InProgress:
          this.rightStatus = ReleaseIssueStatus.Complete;
          this.rightStatusChangeAvailable = true;
          this.leftStatus = ReleaseIssueStatus.Assigned;
          this.leftStatusChangeAvailable = true;
          break;
        case ReleaseIssueStatus.Complete:
          this.rightStatus = ReleaseIssueStatus.CompleteNonIssue;
          this.rightStatusChangeAvailable = true;
          this.leftStatus = ReleaseIssueStatus.InProgress;
          this.leftStatusChangeAvailable = true;
          break;
        case ReleaseIssueStatus.CompleteNonIssue:
          this.leftStatus = ReleaseIssueStatus.Complete;
          this.leftStatusChangeAvailable = true;
          break;
      }
    }
  }
  resetStatusMarkers() {
    this.leftStatus = undefined;
    this.rightStatus = undefined;
    this.leftStatusChangeAvailable = false;
    this.rightStatusChangeAvailable = false;
  }

  isAssignUserMode = false;
  searchUsersField = new FormControl();
  users: SelectableItem[] | undefined;
  filteredUsers: SelectableItem[] | undefined;
  doneSelectingTemplates: boolean = false;
  selectUser(user: SelectableItem) {
    if (this.releaseIssue && user?.item && user?.id) {
      this.releaseIssue.assignedTo = user.id;
      this.releaseIssue.assignedToName = user.item;
      this.isAssignUserMode = false;
    }
  }
  assignToMe() {
    const userId = this.authService.getUserId();
    const userName = this.authService.getUserName();
    if (userId && userName && this.releaseIssue) {
      this.releaseIssue.assignedTo = userId;
      this.releaseIssue.assignedToName = userName;
    }
  }
  loadUsers() {
    let request = new ListUsersRequest();
    request.pageNumber = 1;
    request.pageSize = 300;
    request.enabledOnly = true;

    if (!this.data.isSituUser) {
        let companies = this.authService.listCompanies();
        let userPromises = companies.map((company: Company) => {
            let companyRequest = { ...request };
            companyRequest.companyId = company.id;
            return this.identityWebApiClient.listUsers(request);
        });

        return Promise.all(userPromises)
            .then((responses: ListUsersResponse[]) => {
                let allUsers: SelectableItem[] = [];
                responses.forEach((response) => {
                    if (response.success && response.items) {
                        let users = response.items.map(x => new SelectableItem(x.email!, false, x.id));
                        allUsers = allUsers.concat(users);
                    } else {
                        this.showMessage("There was an issue retrieving users for a company!");
                    }
                });
                this.users = allUsers;
                this.filteredUsers = [...this.users];
            })
            .catch(() => {
              //this.showMessage("There was an error loading users.");
            })
            .finally();
    } else {
        return this.identityWebApiClient.listUsers(request)
            .then((response: ListUsersResponse) => {
                if (response.success && response.items) {
                    this.users = response.items.map(x => new SelectableItem(x.email!, false, x.id));
                    this.filteredUsers = [...this.users];
                } else {
                    this.showMessage("There was an issue retrieving users!");
                }
            })
            .catch(() => {
              //this.showMessage("There was an error loading users.");
            })
            .finally();
    }
  }
  doUserFilter = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredUsers = this.users?.filter(e => { return e.item?.toLowerCase().includes(searchTerm)});
    } else {
      this.filteredUsers = this.users;
    }
  }

  isReportedSituLinkEditMode: boolean = false;
  productNames = ProductName;
  quickViewReportedSituLink: boolean = false;
  quickViewProductName: ProductName | undefined;
  quickViewReportedSituLinkParams: { [key: string]: string } | undefined;
  saveIssueLocation() {
    this.isReportedSituLinkEditMode = false;
  }
  toggleQuickViewReportedSituLink(toggle: boolean) {
    this.quickViewReportedSituLink = toggle;
    if (toggle) {
      let queryParams = this.getQueryParamsFromUrl(this.releaseIssue!.reportedSituLink!);
      if (!queryParams["id"]) return;
      let request = new GetSituFacadeRequest();
      request.renderedOutputId = queryParams["id"];
      this.situViewApiClient.getSituFacade(request)
        .then((response: GetSituFacadeResponse) => {
          if (response.success && response.id !== Guid.EMPTY) {
            this.quickViewProductName = response.productName;
            this.quickViewReportedSituLinkParams = queryParams;
          }
        })
    } else {
      this.quickViewProductName = undefined;
      this.quickViewReportedSituLinkParams = undefined;
    }
  }
  private getQueryParamsFromUrl(url: string): { [key: string]: string } {
    try {
      const parsedUrl = new URL(url);
      const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
      const guid = pathSegments[pathSegments.length - 1];
      const searchParams = new URLSearchParams(parsedUrl.search);
      const queryParams: { [key: string]: string } = {};
      searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });
      queryParams['heightoverride'] = "267px";
      queryParams['quickview'] = "true";
      queryParams['id'] = guid;
      return queryParams;
    } catch (error) {
      return {};
    }
  }

  searchReleaseIssuesField = new FormControl();
  releaseIssues: ReleaseIssue[] = [];
  filteredReleaseIssues: ReleaseIssue[] | undefined;
  confirmedNewIssue: boolean = false;
  private loadReleaseIssues() {
    let request = new ListReleaseIssuesRequest();
    request.companyId = this.data?.companyId;
    //request.releaseId = this.id;
    request.includeStats = true;

    this.situViewApiClient.listReleaseIssues(request)
      .then((response: ListReleaseIssuesResponse) => {
        if (response.success && response.items) {
          this.releaseIssues = response.items;
          this.filteredReleaseIssues = [...this.releaseIssues];
        }
      })
      .catch((err: any) => {

      })
      .finally(() => {
        //this.loading = false;
      });
  }
  doReleaseIssueFilter = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredReleaseIssues = this.releaseIssues?.filter(e => { return e.description?.toLowerCase().includes(searchTerm)});
    } else {
      this.filteredReleaseIssues = this.releaseIssues;
    }
  }

  //tags
  resourceTypeEnum = ResourceType.Any;
  addTag(tag: Tag) {
    if (tag && this.releaseIssue) {
      let request = new AssignReleaseIssueTagRequest();
      request.companyId = this.data.companyId;
      request.tagId = tag.id;
      request.releaseIssueId = this.releaseIssue?.id;
  
      this.situViewApiClient.assignReleaseIssueTag(request)
        .then((response: AssignReleaseIssueTagResponse) => {
          if (response.success && this.releaseIssue?.releaseIssueTags) {
            let releaseIssueTag = new ReleaseIssueTag();
            releaseIssueTag.releaseIssueId = this.releaseIssue.id;
            releaseIssueTag.tagId = tag.id;
            this.releaseIssue.releaseIssueTags.push(releaseIssueTag);
          }
        });
    }
  }

  removeTag(tag: Tag) {
    if (tag && this.releaseIssue) {
      let request = new RemoveReleaseIssueTagRequest();
      request.companyId = this.data.companyId;
      request.tagId = tag.id;
      request.releaseIssueId = this.releaseIssue?.id;
  
      this.situViewApiClient.removeReleaseIssueTag(request)
        .then((response: RemoveReleaseIssueTagResponse) => {
          if (response.success && this.releaseIssue?.releaseIssueTags) {
            this.releaseIssue!.releaseIssueTags = this.releaseIssue.releaseIssueTags?.filter(x => x.tagId !== tag.id);
          }
        });
    }
  }

  tags: Tag[] | undefined;
  private loadTags() {
    let request = new ListTagsRequest();
    request.companyId = this.data.companyId;
    request.resourceType = this.resourceTypeEnum;

    return this.situViewApiClient.listTags(request)
      .then((response: ListTagsResponse) => {
        if (response.success) {
          this.tags = response.tags;
        }
      });
  }
  getAssignedTagIds(item: any): string[] {
    if (!item || !item.releaseIssueTags) {
      return [];
    }
    
    return item.releaseIssueTags.map((x: ReleaseIssueTag) => x.tagId || '');
  }
  //tags

  releaseItems: ReleaseItem[] = [];
  private loadReleaseItems() {
    let request = new ListReleaseItemsRequest();
    request.companyId = this.data.companyId;
    request.renderedOutputId = this.data.renderedOutputId;
    this.situViewApiClient.listReleaseItems(request)
      .then((response: ListReleaseItemsResponse) => {
        if (response.success && response.items) {
          this.releaseItems = response.items;
        }
      })
      .catch((err: any) => {

      })
      .finally(() => {

      });
  }

  private loadReleaseIssue(releaseIssueId: string) {
    if (!releaseIssueId) {
      return;
    } 

    let request = new GetReleaseIssueRequest();
    request.companyId = this.data.companyId;
    request.releaseIssueId = releaseIssueId;
    this.situViewApiClient.getReleaseIssue(request)
      .then((response: GetReleaseIssueResponse) => {
        if (response.success && response.releaseIssue) {
          this.releaseIssue = response.releaseIssue;
          if (!this.releaseIssue.releaseIssueCategory) this.releaseIssue.releaseIssueCategory = this.releaseIssueCategories.Drawing;
          this.determineAvailableStatusChanges();
          this.loadTags();
        }
      })
      .catch((err: any) => {

      })
      .finally(() => {

      });
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

export interface ReleaseIssueDialogComponentData {
  isSituUser: boolean;
  companyId: string;
  releaseIssueId: string;
  releaseId: string;
  existingReleaseIssueIds: string[];
  linkedReleaseItemIds: string[];
  reportedSituLink: string;
  reportedSituLinkProductName: ProductName;
  renderedOutputId: string;
}

export class ReleaseIssueDialogComponentResultData {
  refreshNeeded: boolean = false;
}
